import type { ReadableAtom } from 'nanostores'

import { useStore } from '@nanostores/react'
import clsx from 'clsx'
import { memo, type MouseEvent, type ThHTMLAttributes } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
	OrderKey,
	type OrderParam,
	Param,
	type SortValuesMode,
	URLSearchParser
} from 'shared/search-adapters'

import { StackIcon } from '../StackIcon'
import css from './ContactsTable.module.css'
import { ResizeButton } from './ResizeButton'

const FullOrder = {
	[OrderKey.Ascending]: 'ascending',
	[OrderKey.Descending]: 'descending'
} as const

const SimpleHeadCell = ({
	children,
	className,
	resizeVar,
	...props
}: ThHTMLAttributes<HTMLTableCellElement> & {
	resizeVar?: string
}) => (
	<th className={clsx(css.headCell, className)} {...props}>
		{children}
		{resizeVar && <ResizeButton value={resizeVar} />}
	</th>
)

function SortableHeadCell({
	children,
	className,
	onSortChange,
	value,
	...props
}: ThHTMLAttributes<HTMLTableCellElement> & {
	onSortChange: (event: MouseEvent<HTMLButtonElement>) => void
	value: string
}) {
	const currentSort = props['aria-sort']
	const nextOrder: 'ascending' | 'descending' =
		currentSort === 'ascending' ? 'descending' : 'ascending'

	const label = `Sort as ${nextOrder}`

	return (
		<th
			className={clsx(
				css.headCell,
				currentSort !== 'none' && css.sortActive,
				className
			)}
			{...props}
		>
			<button
				aria-description={label}
				className={css.sortButton}
				onClick={onSortChange}
				type='button'
				value={value}
			>
				{children}
				<StackIcon
					className={clsx(
						css.sortIcon,
						currentSort === 'descending' && css.descending
					)}
					icon='ascend'
					width={18}
				/>
			</button>
			{value && <ResizeButton value={value} />}
		</th>
	)
}

function SortableHeadCells_<K extends string = string>({
	allowedKeys = 'General',
	columnsStore,
	headlineDictionary,
	sortDictionary,
	sortKey = Param.Order,
	...props
}: ThHTMLAttributes<HTMLTableCellElement> & {
	allowedKeys?: SortValuesMode
	columnsStore: ReadableAtom<K[]>
	headlineDictionary: Record<K, string>
	sortDictionary: Record<K, string>
	sortKey?: OrderParam
}) {
	const [params, setParams] = useSearchParams()

	const activeColumns = useStore(columnsStore)

	const { orderBy, sortBy } = URLSearchParser.Sort(params, sortKey, allowedKeys)

	const onSortChange = (event: MouseEvent<HTMLButtonElement>) => {
		const key = event.currentTarget.value as K
		const nextSort = sortDictionary[key]

		const isActive = sortBy === nextSort
		let nextOrder: OrderKey = OrderKey.Ascending

		if (isActive && orderBy === OrderKey.Ascending) {
			nextOrder = OrderKey.Descending
		}

		const pageKey = sortKey === Param.OrderModal ? Param.ModalSkip : Param.Skip

		setParams(prev => {
			prev.set(sortKey, `${nextSort} ${nextOrder}`)
			prev.delete(pageKey)
			return prev
		})
	}

	return activeColumns.map(key => {
		const children = headlineDictionary[key]

		const sortKey = sortDictionary[key]

		const isActive = sortBy === sortKey

		return key in sortDictionary ? (
			<SortableHeadCell
				aria-sort={isActive ? FullOrder[orderBy] : 'none'}
				key={key}
				onSortChange={onSortChange}
				value={key}
				{...props}
			>
				{children}
			</SortableHeadCell>
		) : (
			<SimpleHeadCell key={key} resizeVar={key} {...props}>
				{children}
			</SimpleHeadCell>
		)
	})
}

export const SortableHeadCells = memo(SortableHeadCells_)
